
// Only updating the fetchBookings method to ensure it properly loads data
import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { getUserGymId } from '@/utils/gymAuth';
import { Booking, getUserBookings } from '@/services/bookingsService';

interface BookingsState {
  bookings: Booking[];
  isLoading: boolean;
  error: string | null;
  
  fetchBookings: () => Promise<void>;
  updateBooking: (bookingId: string, data: any) => Promise<boolean>;
  clearBookings: () => void; // Clear all bookings on gym switch
}

export const useBookingsSupabaseStore = create<BookingsState>((set, get) => ({
  bookings: [],
  isLoading: false,
  error: null,
  
  // ✅ Clear all bookings on gym switch
  clearBookings: () => {
    console.log('[bookingsStore] Clearing all bookings cache');
    set({ bookings: [], error: null });
  },
  
  fetchBookings: async () => {
    const { isLoading } = get();
    
    // Guard contre appels concurrents
    if (isLoading) {
      console.log('[bookingsStore] Already fetching, skipping');
      return;
    }
    
    // NE PAS reset bookings, garder les anciennes pendant le chargement
    set({ isLoading: true, error: null });
    
    try {
      console.log("Fetching bookings via edge function...");
      
      // `getUserBookings` lève désormais en cas d'échec : un tableau vide
      // signifie réellement « aucune réservation », plus « la requête a raté ».
      const bookingsData = await getUserBookings({});

      console.log(`Fetched ${bookingsData.length} bookings from edge function`);

      set({ bookings: bookingsData, isLoading: false, error: null });
    } catch (error: any) {
      // On garde les réservations déjà affichées mais on pose l'état d'erreur :
      // sans lui, l'écran affichait « Aucune réservation à venir » sur une
      // simple panne réseau et l'adhérent pouvait double-réserver.
      console.error('Error in fetchBookings:', error);
      set({ error: error?.message || 'Failed to fetch bookings', isLoading: false });
    }
  },

  
  updateBooking: async (bookingId: string, data: any) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      
      const { error } = await supabase.functions.invoke('bookings-management', {
        body: {
          action: 'update_booking',
          gym_id: gymId,
          bookingId,
          data
        }
      });
      
      if (error) {
        console.error('Error updating booking:', error);
        set({ error: error.message, isLoading: false });
        return false;
      }
      
      // Update local state
      await get().fetchBookings();
      
      return true;
    } catch (error: any) {
      console.error('Error in updateBooking:', error);
      set({ error: error.message, isLoading: false });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
  
}));
